import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../../components/Navbar";
import { BASE_URL } from "../../../const";

const URL = `${BASE_URL}/task-aggregation-data/`;

export default function TaskAggregationPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingTasks, setGeneratingTasks] = useState({});
  const [taskCounts, setTaskCounts] = useState({});
  const [warehouse, setWarehouse] = useState(localStorage.getItem("selectedWarehouse"));
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
 
  // initialize admin & warehouse preference
  useEffect(() => {
    const store = localStorage.getItem("store");
    if (store === "admin") {
      setIsAdmin(true);
      const saved = localStorage.getItem("selectedWarehouse");
      if (saved) setWarehouse(saved);
    } else {
      // map store to warehouse for non-admin users (adjust strings if needed)
      if (store === "SELECT BRANDS (I) PVT. LTD. (PUNE)") setWarehouse("pune");
      else if (store === "SELECT BRANDS (I) PVT. LTD. (KOLKATA)") setWarehouse("kolkata");
    }
  }, []);

  // fetch whenever warehouse changes
  useEffect(() => {
    fetchAggregationData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouse]);

  const fetchAggregationData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token") || "";
      const fetchUrl = `${URL}?warehouse=${encodeURIComponent(warehouse)}`;
      const res = await fetch(fetchUrl, {
        headers: {
          Authorization: token ? `Token ${token}` : "",
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        console.error("Failed to fetch aggregation data:", res.status);
        setData([]);
        return;
      }

      const json = await res.json();
      // backend might return array or object; handle common shapes
      const items = Array.isArray(json) ? json : json.data || json.results || json.items || [];
      setData(items);
    } catch (err) {
      console.error("Error fetching data:", err);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (layer) => {
    navigate(`/layer/${layer}`);
  };

  const handleGenerateTasks = async (layer) => {
    try {
      setGeneratingTasks(prev => ({ ...prev, [layer]: true }));
      const token = localStorage.getItem("token") || "";
      // include warehouse as query param on POST endpoint too
      const genUrl = `${BASE_URL}/auto-task-generate/${layer}/?warehouse=${encodeURIComponent(warehouse)}`;

      const res = await fetch(genUrl, {
        method: "POST",
        headers: {
          Authorization: token ? `Token ${token}` : "",
          "Content-Type": "application/json",
        },
        // no body required; if backend expects JSON, add it here
      });

      let result;
      try {
        result = await res.json();
      } catch (e) {
        result = {};
      }

      if (res.ok) {
        // normalize created count from common response shapes
        const createdCount = result.tasks?.created_count ?? result.total_tasks_created ?? result.created_count ?? 0;
        setTaskCounts(prev => ({ ...prev, [layer]: createdCount }));

        // refresh aggregation data (include warehouse)
        await fetchAggregationData();
      } else {
        console.error("Error generating tasks:", result);
      }
    } catch (err) {
      console.error("Error generating tasks:", err);
    } finally {
      setGeneratingTasks(prev => ({ ...prev, [layer]: false }));
    }
  };

  const handleAdminClick = () => {
    navigate("/warehouse-admin-page");
  };

  const handleWarehouseChange = (e) => {
    const newWh = e.target.value;
    setWarehouse(newWh);
    localStorage.setItem("selectedWarehouse", newWh);
  };

  const getLayerColor = (layer) => {
    const colors = {
      1: "bg-purple-600",
      2: "bg-green-600",
      3: "bg-blue-600",
      4: "bg-orange-600",
      5: "bg-red-600",
      6: "bg-indigo-600",
    };
    return colors[layer] || "bg-gray-600";
  };

  return (
    <>
      <Navbar />
      <div className="p-4 max-w-7xl mx-auto bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
        {/* Header */}

        <div className="w-full bg-white shadow-sm rounded-xl p-3 md:p-4 mb-4">
  <div className="flex items-start justify-between gap-3">

    {/* Left: Back */}
    <div className="flex-shrink-0">
      <button
        onClick={() => navigate("/inventory-management-dashboard")}
        className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition border border-gray-300"
        title="Back"
      >
        <svg className="w-4 h-4 text-gray-900" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
      </button>
    </div>

    {/* Center: Title + Subtitle + Warehouse */}
    <div className="flex-1 text-center min-w-0">
      <h2 className="text-lg md:text-xl font-semibold text-gray-900">Layer wise overview</h2>
      <p className="text-[11px] md:text-xs text-gray-500 mt-1">Overview of pending tasks</p>
      {isAdmin && (
      <div className="mt-3 flex justify-center">
        <select
          value={warehouse}
          onChange={handleWarehouseChange}
          className="px-3 py-2 text-sm border border-gray-200 rounded-md bg-white min-w-[140px]"
        >
          <option value="pune">Pune Warehouse</option>
          <option value="kolkata">Kolkata Warehouse</option>
        </select>
      </div>)}
    </div>

    {/* Right: Buttons stacked vertically */}
    <div className="flex-shrink-0 flex flex-col items-end gap-2">

      {/* Dashboard button */}
      <button
        onClick={() => navigate("/batch-viewer")}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800 text-white text-sm font-semibold hover:opacity-95"
          aria-label="Open admin panel"
        >
        Dashboard
      </button>

      {/* Admin button styled same as Dashboard */}
      {isAdmin && (
        <button
        onClick={() => navigate("/warehouse-admin-page")}
        className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-800 text-white text-sm font-semibold hover:opacity-95"
          aria-label="Open admin panel"
        >
          Admin
        </button>
      )}
    </div>
  </div>
</div>


        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center h-40 bg-white rounded-lg border border-gray-200">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="mt-3 text-sm text-gray-600">Loading task data...</p>
            </div>
          </div>
        ) : data.length === 0 ? (
          <div className="bg-white rounded-lg p-6 text-center border border-gray-200">
            <svg className="mx-auto h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m0 0V9m0 8h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="text-sm text-gray-600 mt-2">No tasks available</p>
          </div>
        ) : (
          <div className="space-y-3">
          {(() => {
            // hide layer 1 when selected warehouse is "kolkata"
            // const hideLayerOne = String(selectedWarehouse ?? "").trim().toLowerCase() === "kolkata";
            const visibleData = (data ?? []).filter(item => {
              const layer = item.layer ?? null;
              if (warehouse === "kolkata" && layer === 1) return false;
              return true;
            });

            return visibleData.map((item, idx) => {
              const layer = item.layer ?? idx;
              return (
                <div
                  key={layer}
                  onClick={() => handleCardClick(layer)}
                  className="group cursor-pointer bg-white rounded-lg border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all duration-200 p-3"
                >
                  {/* Layer Header */}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${getLayerColor(item.layer)}`}></div>
                      <span className="text-sm font-semibold text-gray-900">Layer {item.layer}</span>
                    </div>
                    <svg className="w-4 h-4 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <div className="bg-orange-50 rounded p-2 border border-orange-200">
                      <div className="text-xs text-gray-600 mb-1">Pending Tasks</div>
                      <div className="text-lg font-bold text-orange-600">{item.pending_task_count ?? 0}</div>
                    </div>
                    <div className="bg-red-50 rounded p-2 border border-red-200">
                      <div className="text-xs text-gray-600 mb-1">Qty Pending</div>
                      <div className="text-lg font-bold text-red-600">{(item.total_qty_pending ?? 0).toLocaleString()}</div>
                    </div>
                  </div>

                  {/* Generate Tasks Button */}
                  <div className="flex items-center justify-between">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGenerateTasks(item.layer);
                      }}
                      disabled={generatingTasks[item.layer]}
                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-xs font-semibold transition"
                    >
                      {generatingTasks[item.layer] ? (
                        <>
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          Generate Tasks
                        </>
                      )}
                    </button>

                    {/* Generated Tasks Count */}
                    {taskCounts[item.layer] > 0 && (
                      <div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded font-semibold">
                        +{taskCounts[item.layer]} generated
                      </div>
                    )}
                  </div>
                </div>
              );
            });
          })()}
        </div>

        )}
      </div>
    </>
  );
}
